import { Button, IconButton } from "../../components-v2/Button";
import { FC, useMemo } from "react";
import { observer } from "mobx-react";
import { DataTable, TableColumn } from "../../components-v2/DataTable";
import { DecisionsByCategory, NewProjectController } from "./projectListDomain";
import { isOk, SubscriptionTypeModel } from "../../common/common";
import {
  decisionCategoryLabels,
  getDecisionActionLabel,
  isDecisionTypeNew,
  isDecisionTypePreview,
  ProjectListItem,
} from "../common/commonDomain";

import { requiresBasicSubscription, requiresProSubscription } from "../../payment/Payment";
import { Checkbox } from "../../components-v2/Checkbox";
import {
  ChangePermissionDialogButton,
  ConfirmDeleteProjectDialog,
} from "../project/projectComponents";
import { Box, VBox } from "../../components-v2/Box";
import { Tag } from "../../components-v2/Tag";
import { classNames } from "../../utils/classNames";
import { Icon } from "../../components-v2/Icon";
import { DecisionType } from "../../common/decisions";
import { useLocaleFormat, useTranslation, useUserStore } from "../../stores/RootStoreContext";
import { compact } from "lodash";
import { InfoButton } from "../../components-v2/InfoButton";
import { PaidSubscription, SubscriptionType } from "../../common/subscription";
import { ProjectStatusTag } from "../project/commonProjectComponents";

export const DecisionLabel: FC<{ decisionType: DecisionType; isDisabled?: boolean }> = ({
  decisionType,
  isDisabled,
}) => {
  const { t } = useTranslation();
  const badge = isDisabled
    ? t("dbot.coming-soon")
    : isDecisionTypeNew(decisionType)
      ? t("dbot.new")
      : isDecisionTypePreview(decisionType)
        ? t("dbot.preview")
        : null;
  return (
    <div>
      {t(getDecisionActionLabel(decisionType))}{" "}
      {badge && (
        <Tag label={badge} color={isDecisionTypeNew(decisionType) ? "success" : "info"} compact />
      )}
    </div>
  );
};

export const WhatWouldYouLikeToDo: FC<{
  decisionsByCategory: DecisionsByCategory[];
  toggleDecision: (decision: DecisionType) => void;
}> = observer(({ decisionsByCategory, toggleDecision }) => (
  <DecisionList decisionsByCategory={decisionsByCategory} toggleDecision={toggleDecision} />
));

const DecisionList: FC<{
  decisionsByCategory: DecisionsByCategory[];
  toggleDecision: (decision: DecisionType) => void;
}> = observer(({ decisionsByCategory, toggleDecision }) => {
  const { t } = useTranslation();
  return (
    <div
      className={classNames(
        "tw-columns-1",
        "md:tw-columns-2",
        "xl:tw-columns-3",
        "2xl:tw-columns-4",
      )}
    >
      {decisionsByCategory.map(
        ({ category, decisions, requiredSubscriptionType, currentSubscription }) => (
          <VBox key={category} marginBottom={"lg"} className={"tw-break-inside-avoid"}>
            <DecisionTypeHeader
              label={t(decisionCategoryLabels[category])}
              subscriptionTypeModel={{
                currentSubscription,
                requiredSubscriptionType,
              }}
            />
            {decisions.map(({ decisionType, isSelected, isDisabled, isExisting }) => (
              <div key={decisionType}>
                <Checkbox
                  label={<DecisionLabel decisionType={decisionType} isDisabled={isDisabled} />}
                  onChange={() => toggleDecision(decisionType)}
                  disabled={isDisabled || isExisting}
                  checked={isSelected}
                />
              </div>
            ))}
          </VBox>
        ),
      )}
    </div>
  );
});

export const DecisionTypeHeader = ({
  label,
  subscriptionTypeModel,
}: {
  label: string;
  subscriptionTypeModel: SubscriptionTypeModel;
}) => {
  const { t } = useTranslation();
  return (
    <Box display={"flex"} alignItems={"center"}>
      <strong>{label}</strong>
      {requiresBasicSubscription(subscriptionTypeModel) && (
        <Tag
          tooltip={t("dbot.requiresBasicSubscription.tooltip")}
          label={t("dbot.requiresBasicSubscription.label")}
          color={"success"}
        />
      )}
      {requiresProSubscription(subscriptionTypeModel) && (
        <Tag
          label={t("dbot.requiresProSubscription.label")}
          color={"success"}
          tooltip={t("dbot.requiresProSubscription.tooltip")}
        />
      )}
    </Box>
  );
};

export type SubscriptionProps = {
  currentSubscription: PaidSubscription | undefined;
  requiredSubscriptionType: SubscriptionType | undefined;
  isDisabled: boolean;
  isLoading: boolean;
  onNavigateToPayment: () => void;
};

export const RequireSubscriptionButton: FC<{
  subscriptionProps: SubscriptionProps;
}> = observer(({ subscriptionProps }) => {
  const { t } = useTranslation();
  return (
    <>
      <Button
        variant={"primary"}
        label={t(
          requiresBasicSubscription(subscriptionProps)
            ? "dbot.requiresBasicSubscription.continue"
            : "dbot.requiresProSubscription.continue",
        )}
        onClick={subscriptionProps.onNavigateToPayment}
        loading={subscriptionProps.isLoading}
        disabled={subscriptionProps.isDisabled}
      />
      <InfoButton infoText={t("dbot.requiresProSubscription.tooltip")} />
    </>
  );
});

export const ProjectsTable: FC<{ controller: NewProjectController }> = observer(
  ({ controller }) => {
    const { t } = useTranslation();
    const { formatDate } = useLocaleFormat();
    const { companyInfo } = useUserStore();

    const tableColumns: Array<TableColumn<ProjectListItem>> = useMemo(
      () =>
        compact([
          {
            name: "projectId",
            label: "",
            align: "left",
            width: "30px",
            getValue: ({ isBatchProject }) => <Icon name={isBatchProject ? "folders" : "folder"} />,
          },
          {
            name: "projectName",
            label: t("decisions.name"),
            align: "left",
            width: "50%",
            getValue: (project) => (
              <VBox display="flex">
                <div>{project.projectName}</div>
                {project.isBatchProject && (
                  <div className={"subtle text-sm"}>
                    {project.batchProjectCompanyName
                      ? t("dbot.batchProjectIn", { companyName: project.batchProjectCompanyName })
                      : t("dbot.batchProject")}
                  </div>
                )}
              </VBox>
            ),
          },
          {
            name: "projectStatus",
            label: t("transactions.status"),
            align: "left",
            getValue: ({ projectStatus }) => <ProjectStatusTag status={projectStatus} />,
          },
          {
            name: "updatedTime",
            label: t("transactions.last-edit"),
            align: "left",
            getValue: (project) => (
              <small className={"subtle whitespace-nowrap"}>
                {formatDate(project.updatedTime)}
              </small>
            ),
          },
          {
            name: "sharing",
            label: t("sharing"),
            align: "left",
            getValue: (project) =>
              project.numberOfPermissions > 0 && (
                <>
                  <ChangePermissionDialogButton
                    numberOfpermissions={project.numberOfPermissions}
                    onClick={() => controller.openChangePermissionDialog(project)}
                  />
                </>
              ),
          },
          {
            name: "creation_time",
            label: "",
            stopClick: true,
            notSorted: true,
            getValue: (project) => (
              <Box>
                <IconButton
                  icon={"copy"}
                  tooltip={t("dbot.duplicate")}
                  onClick={() =>
                    controller.openDuplicateProjectDialog(
                      project,
                      t("dbot.duplicate.projectName", { name: project.projectName }),
                    )
                  }
                />
                <IconButton
                  color={"error"}
                  icon={"trash"}
                  tooltip={t("remove")}
                  onClick={() => controller.selectForDelete(project)}
                />
              </Box>
            ),
          },
        ]),
      [controller, formatDate, t],
    );
    if (!isOk(controller.projectsResult) || !companyInfo) return null;

    return (
      <>
        <DataTable
          onRowClick={controller.openProject}
          tableColumns={tableColumns}
          tableData={controller.projects}
          getId={(row) => row.id}
        />
        {controller.isShowDeleteDialog && (
          <ConfirmDeleteProjectDialog
            isBatchProject={controller.projectToDelete?.isBatchProject}
            onDelete={() => controller.deleteSubmit()}
            onCancel={() => controller.selectForDelete(null)}
          />
        )}
      </>
    );
  },
);
